import React, {useCallback, useEffect} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {Box, Grid} from '@material-ui/core';
import {HourglassEmpty} from '@material-ui/icons';
import CustomizedCheckbox from '../../Checkbox/Checkbox';
import IconWithTooltip from '../../Tooltip/IconWithTooltip';
import classNames from 'classnames';
import FileSelect from '../../FileSelect/FileSelect';
import {PromptFormProps, PromptsFormType, promptsFormValidationSchema, useStyles,} from './PromptForm.utils';
import {VoiceFileContentType} from '../../../store/types/Voicemail';
import {prepareOggFile} from '../../UploadFile/UploadFile';
import AudioPlayer from '../../AudioPlayer/AudioPlayer';
import PlayFileButton from '../../PlayFileButton/PlayFileButton';
import {useDispatch, useSelector} from 'react-redux';
import { actions } from '../../../store';
import { ReduxState } from '../../../store/types';
import { menuPromptPlaybackKey } from '../../../utils/playbackIdentity';

type PromptProcessingRowProps = {
    dataQa: string;
};

// a prompt that is configured on the backend but not converted yet has no file
// to show and nothing to upload - only a short processing state
const PromptProcessingRow: React.VFC<PromptProcessingRowProps> = ({dataQa}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    return (
        <Box
            className={classNames(classes.processingRow, classes.centeredGrid)}
            display="flex"
            data-qa={dataQa}
            data-testid={dataQa}
        >
            <HourglassEmpty className={classes.processingIcon} />

            <span className={classes.processingLabel}>
                {t('common:processing')}
            </span>

            <IconWithTooltip
                dataQa={dataQa + '-tooltip'}
                tooltipText={t('tooltips:autoAttendants.promptProcessing')}
            />
        </Box>
    );
};

const PromptForm: React.FC<PromptFormProps> = ({
    handleSetIsValidFunc, 
    callback,
    menuId
}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {values, setFieldValue, errors, setFieldError, initialValues} = useFormikContext<PromptsFormType>();

    const account_info = useSelector(
        (state: ReduxState) => state.autoAttendants.autoAttendantDetails?.extension?.account_info
            ?? state.autoAttendants?.autoAttendantInfo?.account_info
    );
    
    useEffect(() => {

        const validationKeys = Object.keys(promptsFormValidationSchema.fields);
        const errorsKeys = Object.keys(errors);

        const hasErrors = validationKeys.some(key => errorsKeys.includes(key));

        if (hasErrors) {
            callback && callback();
            handleSetIsValidFunc?.('prompts', () => false);

        } else {
            handleSetIsValidFunc?.('prompts', () => true);
        }

    }, [errors, values])

    const inputFileNameResolver = {
        introPromptStatus: 'introPromptFileName',
        menuPromptStatus:'menuPromptFileName',
        onTimeoutPromptStatus:'onTimeoutPromptFileName',
        onUnavailableSelectionPromptStatus:'onUnavailableSelectionPromptFileName'
    };


    const handleCheckBoxChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(field, e.target.checked);
        // @ts-ignore
        setFieldError(inputFileNameResolver[field], undefined);
    }
    
    const renderPlayFileButton = useCallback((id: number) => {
        let promptType: 'intro' | 'menu' | 'disabled' | 'timeout' = 'intro';
        let fileToPlayName: string | null = null;
        let fileChanged = false;
        switch(id) {
            case 0:
                fileToPlayName = values.introPromptFileName;
                fileChanged = values.introPromptFileName != initialValues.introPromptFileName;
                break;
            case 1: 
                promptType = 'menu';
                fileToPlayName = values.menuPromptFileName;
                fileChanged = values.menuPromptFileName != initialValues.menuPromptFileName;
                break;
            case 2: 
                promptType = 'timeout';
                fileToPlayName = values.onTimeoutPromptFileName;
                fileChanged = values.onTimeoutPromptFileName != initialValues.onTimeoutPromptFileName;
                break;
            case 3: 
                promptType = 'disabled';
                fileToPlayName = values.onUnavailableSelectionPromptFileName;
                fileChanged = values.onUnavailableSelectionPromptFileName != initialValues.onUnavailableSelectionPromptFileName;
                break;
        }
        if(fileChanged || !fileToPlayName?.length) return (<></>);
        //@ts-ignore
        const resolvedMenuId: number | undefined = menuId || values?.menuId;
        //0 and -1 are not usable menu ids (unsaved menu), so there is nothing to play
        if (!resolvedMenuId || resolvedMenuId < 1) return (<></>);
        return (
            <PlayFileButton
                files={[{
                    call_recording_id: 'auto_attendant_menu',
                    playbackKey: menuPromptPlaybackKey(resolvedMenuId, id),
                    h323_conf_id: id + '',
                    customDownloadFileAction: (callback, restrictedCallback) => {
                        dispatch(
                            actions.getMenuPromptFileAsBlob.request({
                                codec: 'wav',
                                i_menu: resolvedMenuId,
                                prompt_type: promptType,
                                callback: callback,
                                restrictedCallback: restrictedCallback,
                                accountId: account_info?.id,
                                domain: account_info?.um_domain,
                            }),
                        );
                    }
                }]}
                customClasses={{
                    beforePlaying: classes.playPauseButtonLoading,
                    initial: classes.playPauseButtonNotStarted
                }}
                dataQa={"play-file-icon" + id}
                dataTestId={"play-file-icon-id" + id}
                customPlayText={t("common:play")}
                customStopText={t("common:stop")}
                stopPlayingInsteadOfPause
            />);
    }, [values, menuId, account_info, initialValues]);

    return (
        <div className={classes.inputs}>
            <Grid item className={classes.itemsContainer}>
                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.introPromptStatus}
                        dataQa="intro-status"
                        onChange={(value) =>
                            handleCheckBoxChange('introPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.intro')}
                    />

                    <IconWithTooltip
                        dataQa="intro-tooltip"
                        tooltipText={t('tooltips:autoAttendants.intro')}
                    />
                </Grid>

                {values.introPromptStatus &&
                    (values.introPromptConverting && !values.introPromptFile ? (
                        <PromptProcessingRow dataQa="intro-processing" />
                    ) : (
                        <Box
                            className={classes.file}
                            alignItems="center"
                            display="flex"
                        >
                            <FileSelect
                                nameValue={values.introPromptFileName}
                                onFileChange={(file) => {
                                    let audioFile;
                                    if (
                                        file.type === VoiceFileContentType.VideoOgg
                                    ) {
                                        audioFile = prepareOggFile(file);
                                    }

                                    setFieldValue(
                                        'introPromptFileName',
                                        audioFile ? audioFile.name : file.name,
                                    );
                                    setFieldValue(
                                        'introPromptFile',
                                        audioFile ?? file,
                                    );

                                    errors.introPromptFileName?.length && setFieldError('introPromptFileName', '')
                                }}
                                file={values.introPromptFile}
                                customStyles={{
                                    textField: classes.buttonFileTextField,
                                    buttonStyle: classes.buttonFile,
                                }}
                                helperText={errors.introPromptFileName}
                                customIcon={renderPlayFileButton(0)}
                            />
                        </Box>
                    ))}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.menuPromptStatus}
                        dataQa="menu-status"
                        onChange={(value) =>
                            handleCheckBoxChange('menuPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.menu')}
                    />

                    <IconWithTooltip
                        dataQa="menu-tooltip"
                        tooltipText={t('tooltips:autoAttendants.menu')}
                    />
                </Grid>

                {values.menuPromptStatus &&
                    (values.menuPromptConverting && !values.menuPromptFile ? (
                        <PromptProcessingRow dataQa="menu-processing" />
                    ) : (
                        <Box
                            className={classes.file}
                            alignItems="center"
                            display="flex"
                        >
                            <FileSelect
                                nameValue={values.menuPromptFileName}
                                onFileChange={(file) => {
                                    let audioFile;
                                    if (
                                        file.type === VoiceFileContentType.VideoOgg
                                    ) {
                                        audioFile = prepareOggFile(file);
                                    }

                                    setFieldValue(
                                        'menuPromptFileName',
                                        audioFile ? audioFile.name : file.name,
                                    );
                                    setFieldValue(
                                        'menuPromptFile',
                                        audioFile ?? file,
                                    );

                                    errors.menuPromptFileName?.length && setFieldError('menuPromptFileName', '')

                                }}
                                file={values.menuPromptFile}
                                customStyles={{
                                    textField: classes.buttonFileTextField,
                                    buttonStyle: classes.buttonFile,
                                }}
                                helperText={errors.menuPromptFileName}
                                customIcon={renderPlayFileButton(1)}
                            />
                        </Box>
                    ))}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.onTimeoutPromptStatus}
                        dataQa="on-timeout-status"
                        onChange={(value) =>
                            handleCheckBoxChange('onTimeoutPromptStatus', value)
                        }
                        label={t('screens:autoAttendants.onTimeout')}
                    />

                    <IconWithTooltip
                        dataQa="on-timeout-tooltip"
                        tooltipText={t('tooltips:autoAttendants.onTimeout')}
                    />
                </Grid>

                {values.onTimeoutPromptStatus &&
                    (values.onTimeoutPromptConverting && !values.onTimeoutPromptFile ? (
                        <PromptProcessingRow dataQa="on-timeout-processing" />
                    ) : (
                        <Box
                            className={classes.file}
                            alignItems="center"
                            display="flex"
                        >
                            <FileSelect
                                nameValue={values.onTimeoutPromptFileName}
                                onFileChange={(file) => {
                                    let audioFile;
                                    if (
                                        file.type === VoiceFileContentType.VideoOgg
                                    ) {
                                        audioFile = prepareOggFile(file);
                                    }

                                    setFieldValue(
                                        'onTimeoutPromptFileName',
                                        audioFile ? audioFile.name : file.name,
                                    );
                                    setFieldValue(
                                        'onTimeoutPromptFile',
                                        audioFile ?? file,
                                    );

                                    errors.onTimeoutPromptFileName?.length && setFieldError('onTimeoutPromptFileName', '')

                                }}
                                file={values.onTimeoutPromptFile}
                                customStyles={{
                                    textField: classes.buttonFileTextField,
                                    buttonStyle: classes.buttonFile,
                                }}
                                helperText={errors.onTimeoutPromptFileName}
                                customIcon={renderPlayFileButton(2)}
                            />
                        </Box>
                    ))}

                <Grid
                    className={classNames(
                        classes.centeredGrid,
                        classes.playBeforeGrid,
                    )}
                >
                    <CustomizedCheckbox
                        checked={values.onUnavailableSelectionPromptStatus}
                        dataQa="on-unavailable-status"
                        onChange={(value) =>
                            handleCheckBoxChange('onUnavailableSelectionPromptStatus', value)

                        }
                        label={t(
                            'screens:autoAttendants.onUnavailableSelection'
                        )}
                    />

                    <IconWithTooltip
                        dataQa="on-unavailable-tooltip"
                        tooltipText={t('tooltips:autoAttendants.onUnavailable')}
                    />
                </Grid>

                {values.onUnavailableSelectionPromptStatus &&
                    (values.onUnavailableSelectionPromptConverting && !values.onUnavailableSelectionPromptFile ? (
                        <PromptProcessingRow dataQa="on-unavailable-processing" />
                    ) : (
                        <Box
                            className={classes.file}
                            alignItems="center"
                            display="flex"
                        >
                            <FileSelect
                                nameValue={
                                    values.onUnavailableSelectionPromptFileName
                                }
                                onFileChange={(file) => {
                                    let audioFile;
                                    if (
                                        file.type === VoiceFileContentType.VideoOgg
                                    ) {
                                        audioFile = prepareOggFile(file);
                                    }

                                    setFieldValue(
                                        'onUnavailableSelectionPromptFileName',
                                        audioFile ? audioFile.name : file.name,
                                    );
                                    setFieldValue(
                                        'onUnavailableSelectionPromptFile',
                                        audioFile ?? file,
                                    );

                                    errors.onUnavailableSelectionPromptFile?.length && setFieldError('onUnavailableSelectionPromptFile', '')

                                }}
                                file={values.onUnavailableSelectionPromptFile}
                                customStyles={{
                                    textField: classes.buttonFileTextField,
                                    buttonStyle: classes.buttonFile,
                                }}
                                helperText={errors.onUnavailableSelectionPromptFileName}
                                customIcon={renderPlayFileButton(3)}
                            />
                        </Box>
                    ))}
            </Grid>

            <AudioPlayer
                dataQa={'audio-player-call-queue'}
                dataTestId={'audio-player-call-queue'}
                visible={false}
                customClasses={{
                    rootContainer: classes.audioPlayerRootContainer
                }}
            />
        </div>
    );
};

export default PromptForm;
